import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { cn } from "@/shared/lib/cn";
import { Button, buttonVariants, SettingRow, Spinner, Text } from "@/shared/ui";
import { usePortal } from "../hooks/usePortal";
import { useSubscription } from "../hooks/useSubscription";
import { APP_STORE_SUBSCRIPTIONS_URL, isSubscriptionPlan, toPlanId } from "../lib/plans";
import { UsageMeter } from "./UsageMeter";

/** Map a backend subscription status to a translated label, falling back to raw. */
const STATUS_KEY: Record<string, string> = {
  active: "settings.billing.statusActive",
  trialing: "settings.billing.statusTrialing",
  canceled: "settings.billing.statusCanceled",
  past_due: "settings.billing.statusPastDue",
};

/**
 * Settings → Billing. Current plan + manage/upgrade affordance, subscription
 * status and renewal date, and the usage meters. Owned by the billing feature
 * and injected into the settings shell by `app`. Ships loading + error states.
 */
export function BillingSection() {
  const { t, i18n } = useTranslation();
  const { data, isPending, isError, error, refetch } = useSubscription();
  const portal = usePortal();

  if (isPending) {
    return (
      <div className="flex min-h-40 items-center justify-center">
        <Spinner size={22} label={t("common.loading")} />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex flex-col items-start gap-3">
        <Text variant="body" className="text-ink-soft">
          {t("settings.billing.loadError")}
        </Text>
        <Text variant="body" className="text-[13px] text-ink-faint">
          {error.message}
        </Text>
        <Button variant="ghost" size="sm" onClick={() => void refetch()}>
          {t("settings.billing.retry")}
        </Button>
      </div>
    );
  }

  const planId = toPlanId(data.plan_id);
  const isPaid = isSubscriptionPlan(planId);
  const management = data.capabilities?.subscription_management;
  const billingProvider = management?.provider
    ?? (planId === "pro" || planId === "max" ? "app_store" : "none");
  const statusLabel = STATUS_KEY[data.status] ? t(STATUS_KEY[data.status]) : data.status;

  const periodEnd = data.current_period_end
    ? new Intl.DateTimeFormat(i18n.language, { dateStyle: "medium" }).format(
        new Date(data.current_period_end),
      )
    : null;

  function openPortal(): void {
    portal.mutate(undefined, {
      onSuccess: ({ url }) => {
        window.location.href = url;
      },
    });
  }

  return (
    <section className="flex flex-col">
      <Text variant="h4" as="h2" className="mb-1">
        {t("settings.tabs.billing")}
      </Text>

      <div className="flex flex-col gap-1">
        <SettingRow
          title={t("settings.billing.plan")}
          hint={isPaid ? undefined : t("settings.billing.noSubscriptionHint")}
        >
          <div className="flex items-center gap-3">
            <Text variant="ui" as="span">
              {data.plan_name}
            </Text>
            {isPaid && billingProvider === "polar" && management?.portal_enabled ? (
              <Button
                variant="ghost"
                size="sm"
                onClick={openPortal}
                disabled={portal.isPending}
              >
                {portal.isPending ? (
                  <Spinner size={14} label={t("settings.billing.opening")} />
                ) : null}
                {t("settings.billing.manageBilling")}
              </Button>
            ) : isPaid && billingProvider === "app_store" ? (
              <a
                href={APP_STORE_SUBSCRIPTIONS_URL}
                className={cn(buttonVariants({ variant: "ghost", size: "sm" }))}
              >
                {t("settings.billing.manageAppStore")}
              </a>
            ) : isPaid ? (
              <a
                href="mailto:support@anyaistudio.com?subject=Subscription%20management"
                className={cn(buttonVariants({ variant: "ghost", size: "sm" }))}
              >
                {t("settings.billing.contactSupport")}
              </a>
            ) : (
              <Link to="/pricing" className={cn(buttonVariants({ variant: "accent", size: "sm" }))}>
                {t("settings.billing.upgrade")}
              </Link>
            )}
          </div>
        </SettingRow>

        {isPaid ? (
          <SettingRow title={t("settings.billing.status")}>
            <Text variant="ui" as="span" className="capitalize">
              {statusLabel}
            </Text>
          </SettingRow>
        ) : null}

        {isPaid && periodEnd ? (
          <SettingRow title={t("settings.billing.renewal")}>
            <Text variant="body" as="span" className="text-ink-soft">
              {data.cancel_at_period_end
                ? t("settings.billing.cancelsOn", { date: periodEnd })
                : t("settings.billing.renewsOn", { date: periodEnd })}
            </Text>
          </SettingRow>
        ) : null}
      </div>

      {portal.isError ? (
        <Text variant="body" className="mt-3 text-[13px] text-ink-soft">
          {t("settings.billing.portalError")}
        </Text>
      ) : null}

      <Text variant="label" as="h3" className="mb-1 mt-8">
        {t("settings.billing.usage.title")}
      </Text>
      <UsageMeter premium={isPaid} />
    </section>
  );
}
